import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Button,
  Chip,
  Paper,
  Fade,
  Tooltip
} from '@mui/material';
import {
  Notifications,
  Search,
  MarkEmailRead,
  Clear,
  Circle,
  NotificationsOff
} from '@mui/icons-material';
import {
  Dashboard,
  BookmarkBorder,
  Analytics,
  Settings,
  Message
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { formatDistanceToNow } from 'date-fns';

// Import Firebase functions - ENHANCED imports
import {
  addNotificationUpdateListener,
  initializeAppWithFCM,
  testNotification,
  clearAppBadge,
  setAppBadge,
  syncNotificationsFromServiceWorker,
  markNotificationsAsReadInServiceWorker,
  clearNotificationsInServiceWorker
} from '../../firebase';
import { useAuth } from '../../pages/authentication/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';

// Notification item component
const NotificationItem = ({ notification, onMarkAsRead, onRemove }) => {
    const theme = useTheme();
  const navigate = useNavigate();
   const {user} = useAuth();
  const getNotificationIcon = (type) => {
    const iconProps = {
      sx: {
        width: 16,
        height: 16,
        color: theme.palette.text.secondary
      }
    };

    switch (type) {
      case 'message':
        return <Message {...iconProps} />;
      case 'course':
        return <BookmarkBorder {...iconProps} />;
      case 'progress':
        return <Analytics {...iconProps} />;
      default:
        return <Circle {...iconProps} />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'message':
        return theme.palette.info.main;
      case 'course':
        return theme.palette.success.main;
      case 'progress':
        return theme.palette.warning.main;
      default:
        return theme.palette.primary.main;
    }
  };

  return (
    <ListItem
      sx={{
        bgcolor: notification.read ? 'transparent' : alpha(theme.palette.primary.main, 0.04),
        borderLeft: `3px solid ${notification.read ? 'transparent' : getTypeColor(notification.type)}`,
        '&:hover': {
          bgcolor: alpha(theme.palette.action.hover, 0.04)
        },
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }}
      onClick={() => {
        if (!notification.read) {
          onMarkAsRead(notification.id);
        }
        navigate(`/${user.role}/notifications`);
        
      }}
    >
      <ListItemAvatar sx={{ minWidth: 40 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: alpha(getTypeColor(notification.type), 0.1),
            color: getTypeColor(notification.type)
          }}
        >
          {getNotificationIcon(notification.type)}
        </Avatar>
      </ListItemAvatar>

      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: notification.read ? 400 : 600,
                color: theme.palette.text.primary,
                flex: 1
              }}
            >
              {notification.title}
            </Typography>
            {!notification.read && (
              <Circle
                sx={{
                  width: 8,
                  height: 8,
                  color: theme.palette.primary.main
                }}
              />
            )}
          </Box>
        }
        secondary={
          <Box sx={{ mt: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.875rem',
                mb: 0.5
              }}
            >
              {notification.body}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={notification.type}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.75rem',
                  bgcolor: alpha(getTypeColor(notification.type), 0.1),
                  color: getTypeColor(notification.type)
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.disabled }}
              >
                {formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true })}
              </Typography>
            </Box>
          </Box>
        }
      />

      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(notification.id);
        }}
        sx={{
          opacity: 0,
          transition: 'opacity 0.2s',
          '&:hover': { opacity: 1 },
          '.MuiListItem-root:hover &': { opacity: 1 }
        }}
      >
        <Clear sx={{ fontSize: 16 }} />
      </IconButton>
    </ListItem>
  );
};

// Desktop Header Component - ENHANCED
const DesktopHeader = ({ handleProfileMenuOpen }) => {
  const theme = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
  const [selectedNavItem, setSelectedNavItem] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [isFirebaseInitialized, setIsFirebaseInitialized] = useState(false);
  const removeNotificationListener = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  // Navigation items
  const sidebarNavItems = [
    { label: 'Dashboard', icon: Dashboard, badge: null },
    { label: 'My Courses', icon: BookmarkBorder, badge: '12' },
    { label: 'Bookmarks', icon: BookmarkBorder, badge: null },
    { label: 'Progress', icon: Analytics, badge: null },
    { label: 'Messages', icon: Message, badge: '2' },
    { label: 'Settings', icon: Settings, badge: null }
  ];

  // ENHANCED: Notification update handler that syncs with service worker
  const handleNotificationUpdate = useCallback((allNotifications, unreadCount) => {
    console.log('[Header] Received notification update:', {
      total: allNotifications.length,
      unread: unreadCount
    });
    
    setNotifications(allNotifications);
    setAppBadge(unreadCount);
    setIsLoading(false);
  }, []);

  // ENHANCED: Initialize Firebase and load existing notifications
  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        console.log('[Header] Initializing Firebase messaging...');
        
        // Initialize Firebase messaging
        const result = await initializeAppWithFCM();
        if (result.success) {
          setIsFirebaseInitialized(true);
          console.log('[Header] Firebase messaging initialized successfully');
        } else {
          console.warn('[Header] Failed to initialize Firebase messaging:', result.error);
        }

        // IMPORTANT: Load existing notifications immediately
        console.log('[Header] Loading existing notifications...');
        const existingNotifications = await syncNotificationsFromServiceWorker();
        console.log('[Header] Loaded existing notifications:', existingNotifications.length);
        
        if (existingNotifications.length > 0) {
          setNotifications(existingNotifications);
          const unreadCount = existingNotifications.filter(n => !n.read).length;
          setAppBadge(unreadCount);
        }
        
        setIsLoading(false);
        
      } catch (error) {
        console.error('[Header] Error during Firebase initialization:', error);
        setIsLoading(false);
      }
    };

    // Setup the enhanced notification listener for real-time updates
    console.log('[Header] Setting up notification update listener...');
    removeNotificationListener.current = addNotificationUpdateListener(handleNotificationUpdate);

    // Initialize Firebase and load notifications
    initializeNotifications();

    // Cleanup function
    return () => {
      if (removeNotificationListener.current) {
        console.log('[Header] Cleaning up notification listener');
        removeNotificationListener.current();
      }
    };
  }, [handleNotificationUpdate]);

  // ENHANCED: Re-sync notifications when component becomes visible
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        console.log('[Header] App became visible, syncing notifications...');
        try {
          const syncedNotifications = await syncNotificationsFromServiceWorker();
          setNotifications(syncedNotifications);
          const unreadCount = syncedNotifications.filter(n => !n.read).length;
          setAppBadge(unreadCount);
        } catch (error) {
          console.error('[Header] Error syncing on visibility change:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Calculate unread notifications count
  const unreadCount = notifications.filter(n => !n.read).length;

  // Handle notification icon click
  const handleNotificationClick = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  // Handle notification popover close
  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  // ENHANCED: Mark notification as read with service worker sync
  const markAsRead = async (notificationId) => {
    try {
      console.log('[Header] Marking notification as read:', notificationId);
      
      // Optimistically update UI first
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );

      // Sync with service worker
      await markNotificationsAsReadInServiceWorker([notificationId]);
      
    } catch (error) {
      console.error('[Header] Error marking notification as read:', error);
      // Revert optimistic update on error
      const syncedNotifications = await syncNotificationsFromServiceWorker();
      setNotifications(syncedNotifications);
    }
  };

  // ENHANCED: Remove notification with service worker sync
  const removeNotification = async (notificationId) => {
    try {
      console.log('[Header] Removing notification:', notificationId);
      
      // Optimistically update UI first
      setNotifications(prev =>
        prev.filter(notification => notification.id !== notificationId)
      );

      // Sync with service worker
      await clearNotificationsInServiceWorker([notificationId]);
      
    } catch (error) {
      console.error('[Header] Error removing notification:', error);
      // Revert optimistic update on error
      const syncedNotifications = await syncNotificationsFromServiceWorker();
      setNotifications(syncedNotifications);
    }
  };

  // ENHANCED: Mark all as read with service worker sync
  const markAllAsRead = async () => {
    try {
      console.log('[Header] Marking all notifications as read');
      
      // Optimistically update UI first
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, read: true }))
      );
      
      clearAppBadge();

      // Sync with service worker (empty array means mark all as read)
      await markNotificationsAsReadInServiceWorker([]);
      
    } catch (error) {
      console.error('[Header] Error marking all as read:', error);
      // Revert optimistic update on error
      const syncedNotifications = await syncNotificationsFromServiceWorker();
      setNotifications(syncedNotifications);
    }
  };

  // ENHANCED: Clear all notifications with service worker sync
  const clearAllNotifications = async () => {
    try {
      console.log('[Header] Clearing all notifications');
      
      // Optimistically update UI first
      setNotifications([]);
      clearAppBadge();
      handleNotificationClose();

      // Sync with service worker (empty array means clear all)
      await clearNotificationsInServiceWorker([]);
      
    } catch (error) {
      console.error('[Header] Error clearing all notifications:', error);
      // Revert optimistic update on error
      const syncedNotifications = await syncNotificationsFromServiceWorker();
      setNotifications(syncedNotifications);
    }
  };

  // ENHANCED: Test notification function
  const handleTestNotification = async () => {
    try {
      console.log('[Header] Sending test notification...');
      const testNotif = await testNotification();
      console.log('[Header] Test notification sent:', testNotif);
    } catch (error) {
      console.error('[Header] Error sending test notification:', error);
    }
  };

  const isNotificationOpen = Boolean(notificationAnchorEl);

  return (
    <Box
  sx={{
    height: 80,
    bgcolor: alpha(theme.palette.primary.main, 0.06),
    backdropFilter: 'blur(20px)',
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: theme.spacing(4),
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.appBar,
    boxShadow: "0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -1px rgba(0,0,0,0.06)" // 👈 subtle bottom shadow
  }}
>

      {/* Page Title */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
        }}
      >
        Moxy Driving School
      </Typography>

      {/* Header Actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
        {/* Search Icon */}
        <Tooltip title="Search">
          <IconButton
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': { color: theme.palette.primary.main }
            }}
          >
            <Search />
          </IconButton>
        </Tooltip>

        {/* Test Notification Button (Development only) */}
        {/* {process.env.NODE_ENV === 'development' && (
          <Tooltip title="Send Test Notification">
            <Button
              size="small"
              variant="outlined"
              onClick={handleTestNotification}
              disabled={!isFirebaseInitialized}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              Test
            </Button>
          </Tooltip>
        )} */}

        {/* Notifications Icon */}
        <Tooltip title={`${unreadCount} unread notifications`}>
          <IconButton
            onClick={handleNotificationClick}
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': { color: theme.palette.primary.main }
            }}
          >
            <Badge badgeContent={unreadCount} color="error" max={99}>
              {isLoading ? (
                <Notifications sx={{ opacity: 0.5 }} />
              ) : notifications.length === 0 ? (
                <NotificationsOff />
              ) : (
                <Notifications />
              )}
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Profile Avatar */}
        <Avatar
          src={user.profile_pic ? `${process.env.REACT_APP_API_URL}${user.profile_pic}` : undefined}
          sx={{
            width: 40,
            height: 40,
            bgcolor: theme.palette.primary.main,
            cursor: 'pointer',
            border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
            }
          }}
          onClick={handleProfileMenuOpen}
        >
          {`${user.first_name?.charAt(0).toUpperCase() ?? ''}${user.last_name?.charAt(0).toUpperCase() ?? ''}`}
        </Avatar>
      </Box>

      {/* Notification Popover */}
      <Popover
        open={isNotificationOpen}
        anchorEl={notificationAnchorEl}
        onClose={handleNotificationClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 500,
            mt: 1,
            boxShadow: theme.shadows[8],
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }
        }}
      >
        <Paper elevation={0}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Notifications {isLoading && '(Loading...)'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  startIcon={<MarkEmailRead sx={{ fontSize: 16 }} />}
                  onClick={markAllAsRead}
                  disabled={isLoading}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button
                  size="small"
                  startIcon={<Clear sx={{ fontSize: 16 }} />}
                  onClick={clearAllNotifications}
                  color="error"
                  disabled={isLoading}
                  sx={{ fontSize: '0.75rem' }}
                >
                  Clear all
                </Button>
              )}
            </Box>
          </Box>

          {/* Notifications List */}
          {isLoading ? (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                color: theme.palette.text.secondary
              }}
            >
              <Typography variant="body2">
                Loading notifications...
              </Typography>
            </Box>
          ) : notifications.length === 0 ? (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                color: theme.palette.text.secondary
              }}
            >
              <NotificationsOff sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2">
                No notifications yet
              </Typography>
              {process.env.NODE_ENV === 'development' && (
                <Button
                  size="small"
                  onClick={handleTestNotification}
                  sx={{ mt: 1 }}
                  disabled={!isFirebaseInitialized}
                >
                  Send Test Notification
                </Button>
              )}
            </Box>
          ) : (
            <List sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <NotificationItem
                    notification={notification}
                    onMarkAsRead={markAsRead}
                    onRemove={removeNotification}
                  />
                  {index < notifications.length - 1 && (
                    <Divider sx={{ ml: 7 }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          )}

          {/* Footer */}
          {notifications.length > 0 && !isLoading && (
            <Box
              sx={{
                p: 1,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                textAlign: 'center'
              }}
            >
              <Button
                size="small"
                fullWidth
                onClick={() => {
                  handleNotificationClose();
                  // Navigate to notifications page
                   navigate(`/${user.role}/notifications`);
                }}
              >
                View All Notifications
              </Button>
            </Box>
          )}
        </Paper>
      </Popover>
    </Box>
  );
};

export default DesktopHeader